import { useEffect, useId, useRef, useState } from "react";
import type { ReactNode } from "react";
import "./Card.css";
import "./Page.css";
import { tx } from "../i18n";
import { Icon } from "./Icon";
import { helpFor } from "./Help";
import { currentRoute } from "../lib/route";

/* ══ 본문 뼈대 — M9 A안(Figma 5-78, 사람 답 q-0b9741200958cbea9ee77975) ══
   폭은 내용이 정한다: **목록형 본문은 가득**, **폼형 본문은 폼 열 72ch + 오른쪽 보조 열**.
   화면 전체에는 max-width 를 두지 않는다 — 폭을 두는 것은 폼 열 하나뿐이다
   (docs/decisions/design-system.md 「본문 뼈대」). */

/** 페이지 머리 — 공통 v3 의 한 줄 판 `.b-page-bar`: 「제목 (?) │ b3x-in ······ [동작]」(CODE-MOPS 와 같은 모양,
 *  t-in-titlebar-codemops). 주 화면은 전부 이것으로 선다 — 화면마다 h1 과 리드를 따로 적으면 간격과 크기가 갈린다.
 *  리드(설명)는 본문에 깔지 않고 (?) 단추가 여는 판(공통 `.b-popover`)에 담는다 — 없으면 (?) 도 안 그린다.
 *  공통 `@mops/page-head-v1` 을 바로 안 쓰는 이유: 그 (?) 는 글자 한 줄 툴팁(`.b-tip`)이라 문단 여럿인 리드(`<p>`)를
 *  못 싣고 Esc 로 닫히지 않는다. 마크업은 그 부품과 같은 v3 클래스다 — 공통이 판을 받게 되면 그쪽으로 옮긴다. */
export function PageHead({
  title,
  meta,
  lead,
  actions,
}: {
  title: ReactNode;
  meta?: ReactNode;
  lead?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <header className="b-page-bar phead">
      <h1 className="b-page-bar-title">{title}</h1>
      {lead ? <PageHelp>{lead}</PageHelp> : null}
      <span className="b-page-bar-sep" aria-hidden="true" />
      <ol className="b-crumbs" aria-label={tx("ui.crumbs")}>
        <li>
          <a href="#/">b3x-in</a>
        </li>
      </ol>
      {meta}
      {actions ? <div className="b-page-bar-actions">{actions}</div> : null}
    </header>
  );
}

/** 제목 옆 (?) — 누르면 설명 판이 열리고, Esc · 바깥 누르기 · 다시 누르기로 닫힌다. 판은 단추 바로 아래 왼쪽에
 *  붙는다(머리 위는 상단 메뉴라 위로 띄우면 가린다). 단추 이름은 「설명」이고 판은 그 단추가 가리키는 영역이다. */
function PageHelp({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLSpanElement>(null);
  const id = useId();
  const route = currentRoute();
  // 도움말 자기 화면에서는 자기에게 가는 링크를 안 그린다.
  const more = route === "/help" ? null : helpFor(route);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);
  return (
    <span className="phead__help" ref={wrap}>
      <button
        type="button"
        className="b-ibtn b-btn-ghost b-ibtn-sm"
        aria-label={tx("ui.help")}
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setOpen((v) => !v)}
      >
        <Icon name="help" />
      </button>
      {open ? (
        <div className="b-popover phead__pop" id={id} role="region" aria-label={tx("ui.help")}>
          <div className="b-popover-item phead__lead">{children}</div>
          {/* (?) → 도움말 절 연결 규칙은 이 한 줄이다 — 어느 절인가는 도움말 표(`HELP_SECTIONS`)의 route 가 정한다. */}
          {more ? (
            <a className="b-popover-item" href={`#/help?s=${more}`}>
              {tx("help.more")} →
            </a>
          ) : null}
        </div>
      ) : null}
    </span>
  );
}

/** 폼형 본문 — 입력칸은 72ch 열에 두고, 남는 오른쪽에 그 화면의 상태 카드를 세운다. 4000px 모니터에서
 *  입력칸이 한 줄로 늘어나면 읽는 것이 아니라 훑게 된다. 좁은 화면(≤960px)에서는 보조 열이 폼 아래로
 *  내려간다(사이드바와 같은 경계). */
export function FormSplit({ children, aside }: { children: ReactNode; aside?: ReactNode }) {
  return (
    <div className="fsplit">
      <div className="fsplit__form">{children}</div>
      {aside ? <aside className="fsplit__aside">{aside}</aside> : null}
    </div>
  );
}

/** 보조 열의 상태 카드 — 이름 · 값 줄 여럿. 값을 모르면 「모름」이라고 적어 넘긴다(빈 칸으로 두지 않는다). */
export function StatCard({ title = tx("ui.statTitle"), rows }: { title?: string; rows: { label: string; value: ReactNode }[] }) {
  return (
    <section className="b-card pstat" aria-label={title}>
      <span className="b-eyebrow">{title}</span>
      {rows.map((r) => (
        <div className="pstat__row" key={r.label}>
          <span className="caption">{r.label}</span>
          <span className="pstat__val">{r.value}</span>
        </div>
      ))}
    </section>
  );
}
